import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { BarChart3, Users, TrendingUp, CreditCard, BookOpen, Calendar, Calculator, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#FACC15", "#22C55E", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Statistiken">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const bookingCounts = stats?.booking_counts || {};
  
  // Booking type distribution data
  const bookingTypeData = [
    { name: "BQ-Abo", value: bookingCounts.bq_abo || 0 },
    { name: "HQ-Abo", value: bookingCounts.hq_abo || 0 },
    { name: "BQ-Intensiv", value: bookingCounts.bq_intensiv || 0 },
    { name: "HQ-Intensiv", value: bookingCounts.hq_intensiv || 0 },
    { name: "Probetraining", value: bookingCounts.probetraining || 0 },
    { name: "Mathe", value: bookingCounts.mathe_grundkurs || 0 },
    { name: "Fachgespräch", value: bookingCounts.fachgespraech || 0 },
  ].filter(d => d.value > 0);

  // Abo status data
  const statusData = [
    { name: "Aktiv", value: stats?.active_abos || 0 },
    { name: "Bald ablaufend", value: stats?.expiring_abos || 0 },
    { name: "Abgelaufen", value: stats?.expired_abos || 0 }
  ];

  // HQ sub-type data
  const hqSubCounts = stats?.hq_sub_counts || {};
  const hqSubData = [
    { name: "HQ Abo Metalltechnik", value: hqSubCounts.metall || 0 },
  ].filter(d => d.value > 0);

  const chartData = stats?.monthly_stats || [];

  // Calculate total bookings
  const totalBookings = Object.values(bookingCounts).reduce((sum, val) => sum + val, 0);

  return (
    <AdminLayout title="Statistiken">
      <div className="space-y-8" data-testid="stats-page">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">Teilnehmer</p>
                  <p className="text-3xl font-bold text-primary">{stats?.total_participants || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">Aktive Abos</p>
                  <p className="text-3xl font-bold text-green-500">{stats?.active_abos || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">Bald ablaufend</p>
                  <p className="text-3xl font-bold text-yellow-500">{stats?.expiring_abos || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">Gesamt Buchungen</p>
                  <p className="text-3xl font-bold text-blue-500">{totalBookings}</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-blue-500/10 flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Booking Type Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <CreditCard className="h-6 w-6 text-primary mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">BQ-Abo</p>
              <p className="text-xl font-bold">{bookingCounts.bq_abo || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <CreditCard className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">HQ-Abo</p>
              <p className="text-xl font-bold">{bookingCounts.hq_abo || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">BQ-Intensiv</p>
              <p className="text-xl font-bold">{bookingCounts.bq_intensiv || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">HQ-Intensiv</p>
              <p className="text-xl font-bold">{bookingCounts.hq_intensiv || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-orange-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Probetraining</p>
              <p className="text-xl font-bold">{bookingCounts.probetraining || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Calculator className="h-6 w-6 text-pink-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Mathe</p>
              <p className="text-xl font-bold">{bookingCounts.mathe_grundkurs || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-teal-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Fachgespräch</p>
              <p className="text-xl font-bold">{bookingCounts.fachgespraech || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Trend */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="flex items-center gap-2 text-lg">
                <BarChart3 className="h-5 w-5 text-primary" />
                Neue Buchungen (6 Monate)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      axisLine={{ stroke: '#27272a' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      axisLine={{ stroke: '#27272a' }}
                      tickLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#09090b', 
                        border: '1px solid #27272a',
                        borderRadius: '4px',
                        color: '#fff'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#FACC15" 
                      strokeWidth={3}
                      dot={{ fill: '#FACC15', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Booking Types Pie */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5 text-primary" />
                Buchungstypen Verteilung
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-72">
                {bookingTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={bookingTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {bookingTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#09090b', 
                          border: '1px solid #27272a',
                          borderRadius: '4px',
                          color: '#fff'
                        }}
                      />
                      <Legend 
                        formatter={(value) => value}
                        wrapperStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Keine Buchungen vorhanden
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Abo Status Distribution */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg">Abo-Status Übersicht</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData} layout="vertical">
                    <XAxis 
                      type="number"
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      axisLine={{ stroke: '#27272a' }}
                      tickLine={false}
                    />
                    <YAxis 
                      dataKey="name" 
                      type="category"
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      axisLine={{ stroke: '#27272a' }}
                      tickLine={false}
                      width={100}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#09090b', 
                        border: '1px solid #27272a',
                        borderRadius: '4px',
                        color: '#fff'
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      <Cell fill="#22C55E" />
                      <Cell fill="#EAB308" />
                      <Cell fill="#EF4444" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* HQ Sub-Types & Quick Stats */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg">Schnellübersicht</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-secondary/30 rounded-sm border border-border text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">HQ Abo Metalltechnik</p>
                  <p className="text-3xl font-bold text-primary">{hqSubCounts.metall || 0}</p>
                </div>
                <div className="p-4 bg-secondary/30 rounded-sm border border-border text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Gesperrt</p>
                  <p className="text-3xl font-bold text-red-500">{stats?.locked_participants || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Fachrichtungen Distribution */}
        {stats?.fachrichtung_counts && Object.keys(stats.fachrichtung_counts).length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg">Industriemeister-Fachrichtungen</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(stats.fachrichtung_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([name, count], index) => (
                    <div key={name} className="p-3 bg-secondary/30 rounded-sm border border-border flex items-center justify-between">
                      <span className="text-sm text-muted-foreground truncate">{name}</span>
                      <span className="font-bold ml-2">{count}</span>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default Stats;
